import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Sidebar from "../Components/Sidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { Toaster, toast } from "react-hot-toast";
import OverlayLoader from "../Components/OverlayLoader";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import {
  fetchMeadiaSourceData,
  fetchAllSubMediaSource,
  fetchUserData,
  GetUserDetailsForLeadLost,
  FilterLeadLostAsync,
} from "../Services/APIService";

interface UserLeadLostSummary {
  userId: number;
  userName: string;
  mobile: string;
  email: string;
  loginUserName: string | null;
  tenantId: number;
  leadLostCount: number;
  leadIds?: number[];   // ✅ add this
}

interface FilterParams {
  tenantId: number;
  fromDate?: string;
  toDate?: string;
  userId?: number;
  sourceId?: number;
  subSourceId?: number;
  leadTypeId?: number;
}

interface MediaSource {
  sourceId: number;
  sourceName: string;
  isActive: boolean;
}

interface SubMediaSource {
  mediaSourceID: number;
  name: string;
  id: number;
  isActive: boolean;
}

interface User {
  userId: number;
  userName: string;
  userTypeId: number;
  isActive: boolean;
  reportTo: number;
}

interface OptionType {
  value: number;
  label: string;
}

const LeadLostReport: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userSummaries, setUserSummaries] = useState<UserLeadLostSummary[]>([]);
  const [searchText, setSearchText] = useState("");
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  // Dropdown data
  const [mediaSources, setMediaSources] = useState<MediaSource[]>([]);
  const [subMediaSources, setSubMediaSources] = useState<SubMediaSource[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredSubSources, setFilteredSubSources] = useState<
    SubMediaSource[]
  >([]);

  // Filter state
  const [filterParams, setFilterParams] = useState<FilterParams>({
    tenantId: parseInt(localStorage.getItem("tenantId") || "1000"),
    fromDate: "",
    toDate: "",
    userId: undefined,
    sourceId: undefined,
    subSourceId: undefined,
    leadTypeId: undefined,
  });
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedPeriod, setSelectedPeriod] = useState<string>("");

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getMenus();
  }, [location.state]);

  // Fetch dropdown data
  const fetchDropdownData = async () => {
    try {
      const sourceResponse = await fetchMeadiaSourceData();
      setMediaSources(sourceResponse.filter((s: MediaSource) => s.isActive));

      const subSourceResponse = await fetchAllSubMediaSource();
      setSubMediaSources(
        subSourceResponse.filter((s: SubMediaSource) => s.isActive)
      );

      const userResponse = await fetchUserData();
      setUsers(
        userResponse.filter(
          (u: User) => (u.userTypeId === 2 || u.userTypeId === 4) && u.isActive
        )
      );
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      toast.error("Failed to load filter options");
    }
  };

  // Fetch Lead Lost Details
  const fetchLeadLostDetails = async () => {
    try {
      setLoading(true);
      const tenantId = localStorage.getItem("tenantId") || "";
      const response = await GetUserDetailsForLeadLost(tenantId);
      console.log("Lead lost details fetched:", response);
      setUserSummaries(response || []);
    } catch (error) {
      console.error("Error fetching lead lost details:", error);
      toast.error("Failed to load lead lost details");
    } finally {
      setLoading(false);
    }
  };

  // Handle period filter change
  // Handle period filter change
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);

    const today = new Date();

    let fromDate = "";
    let toDate = "";

    switch (period) {
      case "today":
        fromDate = formatDate(today);
        toDate = formatDate(today);
        break;

      case "yesterday": {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        fromDate = formatDate(yesterday);
        toDate = formatDate(yesterday);
        break;
      }

      case "thismonth": {
        const firstDay = new Date(
          today.getFullYear(),
          today.getMonth(),
          1
        );

        fromDate = formatDate(firstDay);
        toDate = formatDate(today);
        break;
      }

      case "lastmonth": {
        const firstDay = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1
        );

        const lastDay = new Date(
          today.getFullYear(),
          today.getMonth(),
          0
        );

        fromDate = formatDate(firstDay);
        toDate = formatDate(lastDay);
        break;
      }

      case "3month": {
        const firstDay = new Date(
          today.getFullYear(),
          today.getMonth() - 3,
          1
        );

        fromDate = formatDate(firstDay);
        toDate = formatDate(today); // include current date
        break;
      }

      case "6month": {
        const firstDay = new Date(
          today.getFullYear(),
          today.getMonth() - 6,
          1
        );

        fromDate = formatDate(firstDay);
        toDate = formatDate(today); // include current date
        break;
      }

      default:
        setFilterParams((prev) => ({
          ...prev,
          fromDate: "",
          toDate: "",
        }));
        return;
    }

    setFilterParams((prev) => ({
      ...prev,
      fromDate,
      toDate,
    }));
  };
  const handleLeadTypeChange = (leadTypeId: number) => {
    setFilterParams({
      ...filterParams,
      leadTypeId: leadTypeId || undefined,
    });
  };

  // Apply Filter
  const applyFilter = async () => {
    try {
      setLoading(true);
      const tenantId = localStorage.getItem("tenantId") || "1000";

      const apiFilterParams: any = {
        tenantId: tenantId,
      };

      if (filterParams.fromDate) {
        apiFilterParams.fromDate = filterParams.fromDate;
      }
      if (filterParams.toDate) {
        apiFilterParams.toDate = filterParams.toDate;
      }
      if (filterParams.userId) {
        apiFilterParams.userId = filterParams.userId;
      }
      if (filterParams.sourceId) {
        apiFilterParams.sourceId = filterParams.sourceId;
      }
      if (filterParams.subSourceId) {
        apiFilterParams.subSourceId = filterParams.subSourceId;
      }
      if (filterParams.leadTypeId) {
        apiFilterParams.inquireId = filterParams.leadTypeId;
      }

      const data = await FilterLeadLostAsync(apiFilterParams);
      console.log("Filtered lead lost data:", data);
      setUserSummaries(data || []);
      toast.success("Filter applied successfully");
    } catch (error) {
      console.error("Error applying filter:", error);
      toast.error("Failed to apply filter");
    } finally {
      setLoading(false);
    }
  };

  // Clear Filter
  const clearFilter = () => {
    setFilterParams({
      tenantId: parseInt(localStorage.getItem("tenantId") || "1000"),
      fromDate: "",
      toDate: "",
      userId: undefined,
      sourceId: undefined,
      subSourceId: undefined,
      leadTypeId: undefined,
    });
    setSelectedPeriod("");
    setFilteredSubSources([]);
    fetchLeadLostDetails();
  };

  // Handle lead lost count click - open in new tab
  const handleLeadLostClick = (
    leadIds: number[] | undefined,
    userName: string
  ) => {
    if (!leadIds || leadIds.length === 0) {
      toast.error("No Leads Found");
      return;
    }

    const tenantId = localStorage.getItem("tenantId");

    const url =
      `/#lead-lost-details?leadIds=${leadIds.join(",")}` +
      `&tenantId=${tenantId}` +
      `&userName=${encodeURIComponent(userName)}`;

    window.open(url, "_blank");
  };

  // Export to CSV (Excel compatible)
  const exportToExcel = () => {
    try {
      const headers = ["User Name", "Mobile", "Email", "Lead Lost Count"];

      const rows = filteredUsers.map((user) => [
        user.userName,
        user.mobile,
        user.email,
        user.leadLostCount || 0,
      ]);

      let csvContent = headers.join(",") + "\n";
      rows.forEach((row) => {
        csvContent += row.map((cell) => `"${cell}"`).join(",") + "\n";
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `lead-lost-report-${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Excel file exported successfully");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export Excel file");
    }
  };

  // Export to PDF using browser print
  const exportToPDF = () => {
    try {
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Lead Lost Report</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 1cm;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            h1 {
              text-align: center;
              color: #333;
              margin-bottom: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #a78590;
              color: white;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .count-cell {
              text-align: center;
              font-weight: bold;
            }
            .meta-info {
              margin-bottom: 15px;
              font-size: 12px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <h1>Lead Lost Report</h1>
          <div class="meta-info">
            <strong>Generated on:</strong> ${new Date().toLocaleString()}<br/>
            ${filterParams.fromDate
          ? `<strong>From Date:</strong> ${filterParams.fromDate}<br/>`
          : ""
        }
            ${filterParams.toDate
          ? `<strong>To Date:</strong> ${filterParams.toDate}<br/>`
          : ""
        }
            <strong>Total Records:</strong> ${filteredUsers.length}
          </div>
          <table>
            <thead>
              <tr>
                <th>User Name</th>
                <th>Mobile</th>
                <th>Email</th>
                <th>Lead Lost Count</th>
              </tr>
            </thead>
            <tbody>
              ${filteredUsers
          .map(
            (user) => `
                <tr>
                  <td>${user.userName}</td>
                  <td>${user.mobile}</td>
                  <td>${user.email}</td>
                  <td class="count-cell">${user.leadLostCount || 0}</td>
                </tr>
              `
          )
          .join("")}
            </tbody>
          </table>
        </body>
        </html>
      `;

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
          toast.success("PDF print dialog opened");
        };
      } else {
        toast.error("Please allow pop-ups to export PDF");
      }
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      toast.error("Failed to export PDF");
    }
  };

  useEffect(() => {
    fetchLeadLostDetails();
    fetchDropdownData();
  }, []);

  const handleSourceOnChange = (sourceId: number | undefined) => {
    setFilterParams({ ...filterParams, sourceId, subSourceId: undefined });
  };

  useEffect(() => {
    if (filterParams.sourceId && subMediaSources.length > 0) {
      const filtered = subMediaSources.filter(
        (src) => src.mediaSourceID === filterParams.sourceId
      );
      setFilteredSubSources(filtered);
      if (!filtered.some((s) => s.id === filterParams.subSourceId)) {
        setFilterParams((prev) => ({ ...prev, subSourceId: undefined }));
      }
    } else {
      setFilteredSubSources([]);
      setFilterParams((prev) => ({ ...prev, subSourceId: undefined }));
    }
  }, [filterParams.sourceId, subMediaSources]);

  const filteredUsers = userSummaries.filter(
    (user) =>
      user.userName.toLowerCase().includes(searchText.toLowerCase()) ||
      user.email.toLowerCase().includes(searchText.toLowerCase()) ||
      user.mobile.includes(searchText)
  );

  const getSelectedOption = (options: OptionType[], value?: number) => {
    return options.find((option) => option.value === value) || null;
  };

  const sourceOptions: OptionType[] = mediaSources.map((s) => ({
    value: s.sourceId,
    label: s.sourceName,
  }));

  const subSourceOptions: OptionType[] = filteredSubSources.map((s) => ({
    value: s.id,
    label: s.name,
  }));

  const userTypeId = Number(localStorage.getItem("userTypeId"));
  const loggedUserId = Number(localStorage.getItem("UserId"));
  const isLoginAsManager = localStorage.getItem("isLoginAsManager") === "true";

  let filteredUserList = users;

  // Admin → show all users
  if (userTypeId === 1) {
    filteredUserList = users;
  }

  // Manager
  else if (userTypeId === 2) {
    if (isLoginAsManager) {
      // show manager + team
      filteredUserList = users.filter(
        (u) => u.userId === loggedUserId || u.reportTo === loggedUserId
      );
    } else {
      // manager behaving like normal user
      filteredUserList = users.filter((u) => u.userId === loggedUserId);
    }
  }

  // Normal User
  else {
    filteredUserList = users.filter((u) => u.userId === loggedUserId);
  }

  const userOptions: OptionType[] = filteredUserList.map((u) => ({
    value: u.userId,
    label: u.userName,
  }));
  const columns = [
    {
      name: "User Name",
      selector: (row: UserLeadLostSummary) => row.userName,
      sortable: true,
      width: "250px",
      cell: (row: UserLeadLostSummary) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: "rgb(167, 133, 144)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            {row.userName.charAt(0).toUpperCase()}
          </div>
          <span style={{ fontWeight: "500", color: "#33475b" }}>
            {row.userName}
          </span>
        </div>
      ),
    },
    {
      name: "Email",
      selector: (row: UserLeadLostSummary) => row.email,
      sortable: true,
      width: "250px",
    },
    {
      name: "Lead Lost Count",
      cell: (row: UserLeadLostSummary) => (
        <span
          className="status-count-badge lost-badge"
          onClick={() => handleLeadLostClick(row.leadIds, row.userName)}
          style={{ cursor: "pointer" }}
        >
          {row.leadLostCount || 0}
        </span>
      ),
      width: "150px",
    },
  ];

  return (
    <div className="wrapper">
      <Toaster
        position="top-center"
        reverseOrder={false}
        containerStyle={{ zIndex: 10000 }}
      />
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header carHeaderWithBtn">
                      <div className="card-title mb-0">Lead Lost Report</div>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <input
                          type="text"
                          placeholder="Search by Name, Email or Mobile"
                          className="p-2 datatableSearchInput"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          style={{ width: "250px" }}
                        />
                        <button
                          className="demo-btn btn btn-outline-success btn-sm"
                          onClick={exportToPDF}
                          title="Export to PDF"
                        >
                          <i className="bi bi-file-pdf"></i> Export PDF
                        </button>
                        <button
                          className="demo-btn btn btn-outline-success btn-sm"
                          onClick={exportToExcel}
                          title="Export to Excel"
                        >
                          <i className="bi bi-file-excel"></i> Export Excel
                        </button>
                        <button
                          className="demo-btn btn btn-outline-primary btn-sm"
                          onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                        >
                          <i
                            className={`bi bi-${isFilterExpanded ? "chevron-up" : "funnel"
                              }`}
                          ></i>{" "}
                          {isFilterExpanded ? "Hide Filters" : "Show Filters"}
                        </button>
                      </div>
                    </div>

                    {/* Filter Section */}
                    {isFilterExpanded && (
                      <div
                        className="card-body"
                        style={{
                          borderBottom: "1px solid #dee2e6",
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        <div className="row g-3">
                          <div className="col-md-3">
                            <label className="form-label">Period</label>

                            <select
                              className="form-select form-control-lg"
                              value={selectedPeriod}
                              onChange={(e) => handlePeriodChange(e.target.value)}
                            >
                              <option value="">Select Period</option>

                              <option value="today">Today</option>

                              <option value="yesterday">Yesterday</option>

                              <option value="thismonth">This Month</option>

                              <option value="lastmonth">
                                Last Month (exclude this month)
                              </option>

                              <option value="3month">
                                Last 3 Months (exclude this month)
                              </option>

                              <option value="6month">
                                Last 6 Months (exclude this month)
                              </option>
                            </select>
                          </div>

                          <div className="col-md-3">
                            <label className="form-label">From Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterParams.fromDate}
                              onChange={(e) =>
                                setFilterParams({
                                  ...filterParams,
                                  fromDate: e.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="col-md-3">
                            <label className="form-label">To Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterParams.toDate}
                              onChange={(e) =>
                                setFilterParams({
                                  ...filterParams,
                                  toDate: e.target.value,
                                })
                              }
                            />
                          </div>

                          {/* <div className="col-md-3">
                            <label className="form-label">Lead Type</label>
                            <select
                              className="form-select form-control-lg"
                              value={filterParams.leadTypeId || ""}
                              onChange={(e) =>
                                handleLeadTypeChange(parseInt(e.target.value))
                              }
                            >
                              <option value="">Select Lead Type</option>
                              <option value="1">New Inquiry</option>
                              <option value="6">Reinquiry</option>
                            </select>
                          </div> */}

                          <div className="col-md-3">
                            <label className="form-label">User</label>
                            <Select
                              options={userOptions}
                              value={getSelectedOption(
                                userOptions,
                                filterParams.userId
                              )}
                              onChange={(option) =>
                                setFilterParams({
                                  ...filterParams,
                                  userId: option?.value,
                                })
                              }
                              className="basic-single-select"
                              classNamePrefix="select"
                              placeholder="Select User"
                              isSearchable
                              isClearable
                            />
                          </div>

                          <div className="col-md-4">
                            <label className="form-label">Source</label>
                            <Select
                              options={sourceOptions}
                              value={getSelectedOption(
                                sourceOptions,
                                filterParams.sourceId
                              )}
                              onChange={(option) =>
                                handleSourceOnChange(option?.value)
                              }
                              className="basic-single-select"
                              classNamePrefix="select"
                              placeholder="Select Source"
                              isSearchable
                              isClearable
                            />
                          </div>

                          <div className="col-md-4">
                            <label className="form-label">Sub Source</label>
                            <Select
                              options={subSourceOptions}
                              value={getSelectedOption(
                                subSourceOptions,
                                filterParams.subSourceId
                              )}
                              onChange={(option) =>
                                setFilterParams({
                                  ...filterParams,
                                  subSourceId: option?.value,
                                })
                              }
                              className="basic-single-select"
                              classNamePrefix="select"
                              placeholder={
                                filterParams.sourceId
                                  ? "Select Sub Source"
                                  : "Select source first"
                              }
                              isSearchable
                              isClearable
                              isDisabled={!filterParams.sourceId}
                            />
                          </div>

                          <div
                            className="col-12"
                            style={{
                              display: "flex",
                              gap: "10px",
                              justifyContent: "flex-end",
                            }}
                          >
                            <button
                              className="btn btn-secondary"
                              onClick={clearFilter}
                            >
                              Clear
                            </button>
                            <button
                              className="btn btn-primary"
                              onClick={applyFilter}
                            >
                              Apply Filter
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="card-body p-0">
                      <DataTable
                        columns={columns}
                        data={filteredUsers}
                        pagination
                        paginationPerPage={10}
                        highlightOnHover
                        striped
                        responsive
                        noDataComponent="No lead lost data found"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>

      <OverlayLoader
        loading={loading}
        message="Loading Lead Lost Data"
        submessage="Please wait..."
      />

      <style>{`
        .rdt_TableCol:first-of-type {
          padding-left: 16px !important;
        }
        
        .status-count-badge {
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 13px;
          display: inline-block;
          transition: all 0.2s;
        }
        
        .status-count-badge:hover {
          transform: scale(1.1);
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        
        .lost-badge {
          background: #fce4ec;
          color: #ad1457;
        }

        .css-1nmdiq5-menu {
          position: relative;
          z-index: 10000;
        }
      `}</style>
    </div>
  );
};

export default LeadLostReport;
